/**
 * Confluence: two or more key levels stacked within a few points.
 *
 * Traders read a stack (say, put wall + weekly VAL) as an extreme point where
 * absorption is likely. Whether that is TRUE is an empirical question, so the
 * label is decided by a measurement, not by taste: see CONFLUENCE_CALIBRATION
 * and scripts/gex/confluence_calibration.py. The chart only says "high
 * absorption odds" if the measured hold rate earns it.
 */
import type { KeyLevel } from './keyLevels';
import { isVestFeedSymbol, roundToTick, tickDecimals } from './vestFeed';

export interface ConfluenceZone {
  low: number;
  high: number;
  mid: number;
  members: KeyLevel[];
}

/**
 * Group levels that sit within `tolerance` points of each other.
 *
 * Greedy over price order, and the zone's whole span is bounded by the
 * tolerance (not just neighbour-to-neighbour gaps), so a ladder of levels 8
 * points apart cannot chain into one 40-point "zone". Only groups of two or
 * more levels are returned.
 */
export function computeConfluenceZones(levels: KeyLevel[], tolerance: number): ConfluenceZone[] {
  if (!(tolerance > 0)) return [];
  const sorted = levels
    .filter((l) => Number.isFinite(l.price) && l.price > 0)
    .slice()
    .sort((a, b) => a.price - b.price);
  const zones: ConfluenceZone[] = [];
  let group: KeyLevel[] = [];
  const flush = () => {
    if (group.length >= 2) {
      const low = group[0].price;
      const high = group[group.length - 1].price;
      zones.push({ low, high, mid: (low + high) / 2, members: group });
    }
    group = [];
  };
  for (const l of sorted) {
    if (group.length > 0 && l.price - group[0].price > tolerance) flush();
    group.push(l);
  }
  flush();
  return zones;
}

/** A measured hold rate: held / (held + broke). */
export interface HoldStat {
  held: number;
  total: number;
  ci95: [number, number];
}

export interface ConfluenceCalibration {
  instrument: string;
  firstDay: string;
  lastDay: string;
  sessions: number;
  /** Reversal needed to count as held, points. */
  holdPts: number;
  /** Move through the level that counts as a break, points. */
  breakPts: number;
  tolerancePts: number;
  stacked: HoldStat;
  single: HoldStat;
  stackedWithGex: HoldStat;
  gexSessions: number;
}

/**
 * Result of scripts/gex/confluence_calibration.py on 2026-10-01
 * (scripts/gex/results/confluence_calibration_2026-10-01.json).
 *
 * 269 RTH sessions of raw NQ ticks. A touch "held" if price reversed 10 points
 * back the way it came before breaking 10 points through. Stacked levels held
 * no more often than lone ones, so the chart does NOT claim absorption odds.
 * GEX levels exist for only 11 of those sessions (snapshots began 2026-09-16,
 * and with the old all-expiry wall definitions), far too few to say anything.
 */
export const CONFLUENCE_CALIBRATION: ConfluenceCalibration = {
  instrument: 'NQ',
  firstDay: '2025-09-11',
  lastDay: '2026-10-01',
  sessions: 269,
  holdPts: 10,
  breakPts: 10,
  tolerancePts: 10,
  stacked: { held: 3859, total: 7148, ci95: [0.528, 0.551] },
  single: { held: 10287, total: 18877, ci95: [0.538, 0.552] },
  stackedWithGex: { held: 20, total: 45, ci95: [0.309, 0.588] },
  gexSessions: 11,
};

/**
 * Does the measurement support calling a stack "high absorption odds"?
 *
 * All three must hold: enough touches to mean something, a clear majority
 * holding, and stacked levels beating lone levels with non-overlapping 95%
 * intervals. Anything less is a stack, not an edge.
 */
export function calibrationSupportsAbsorption(c: ConfluenceCalibration): boolean {
  return c.stacked.total >= 30 && c.stacked.held / c.stacked.total >= 0.6 && c.stacked.ci95[0] > c.single.ci95[1];
}

const pct = (s: HoldStat) => (s.total > 0 ? `${((s.held / s.total) * 100).toFixed(1)}%` : 'n/a');

/** The zone's on-chart label. */
export function confluenceLabel(c: ConfluenceCalibration = CONFLUENCE_CALIBRATION): string {
  return calibrationSupportsAbsorption(c)
    ? `EXTREME · high absorption odds (held ${c.stacked.held} of ${c.stacked.total})`
    : 'EXTREME · stacked levels';
}

/** Tooltip lines: what is stacked, then what the history says about stacks. */
export function confluenceTooltip(zone: ConfluenceZone, c: ConfluenceCalibration = CONFLUENCE_CALIBRATION): string[] {
  const members = zone.members
    .slice()
    .sort((a, b) => b.price - a.price)
    .map((m) => `${m.label} ${m.price.toFixed(2)}`)
    .join(' · ');
  const lines = [
    `${zone.members.length} levels within ${(zone.high - zone.low).toFixed(2)} pts: ${members}`,
    `History (${c.instrument}, ${c.sessions} RTH sessions ${c.firstDay} to ${c.lastDay}): stacked levels held ${pct(c.stacked)} ` +
      `(${c.stacked.held}/${c.stacked.total}) vs single levels ${pct(c.single)} (${c.single.held}/${c.single.total}). ` +
      `Held = reversed ${c.holdPts} pts before breaking ${c.breakPts} pts through.`,
  ];
  lines.push(
    calibrationSupportsAbsorption(c)
      ? 'Stacks have held measurably more often than single levels.'
      : `No measured edge: stacks have not held more often than single levels. With GEX in the stack: ${pct(c.stackedWithGex)} (${c.stackedWithGex.held}/${c.stackedWithGex.total}, only ${c.gexSessions} sessions of GEX history).`,
  );
  return lines;
}

// ---------------------------------------------------------------------------
// Vest chart feeds (#630). The tolerance pref and the calibration above are NQ
// points and NQ history. A `.VEST` chart gets the tolerance rescaled to its
// own volatility and a tooltip that does not quote NQ's hold rates. Every
// other symbol goes through the functions above unchanged.
// ---------------------------------------------------------------------------

/**
 * NQ's median 2m true range, points: NQZ6.CME, the last 8,640 2m bars (30
 * days, all hours) as of 2026-10-31. The yardstick that turns the NQ-point
 * tolerance into "this many typical bars" for another market.
 */
export const NQ_REF_MEDIAN_TRUE_RANGE_PTS = 17;
/** NQ's close at that measurement: the fallback scale (17 / 28,543 = 6 bp of price) when a market has too few bars. */
export const NQ_REF_PRICE = 28_543;
/** Closed bars the median true range looks back over (2 days of 2m bars). */
export const TOLERANCE_ATR_LOOKBACK = 1440;
/** Fewer true ranges than this and the price fallback is used. */
export const TOLERANCE_ATR_MIN_BARS = 30;

interface Hlc { high: number; low: number; close: number }

/**
 * Median true range (max(high, prev close) - min(low, prev close)) of the last
 * `lookback` bars. True range, not high - low: a raw Vest trade candle opens
 * at its first print, so a thin market's bars are often a single trade with
 * zero range while the move happened between bars. null below `minBars`.
 */
export function medianTrueRange(candles: readonly Hlc[], lookback = TOLERANCE_ATR_LOOKBACK, minBars = TOLERANCE_ATR_MIN_BARS): number | null {
  const start = Math.max(1, candles.length - lookback);
  const tr: number[] = [];
  for (let i = start; i < candles.length; i++) {
    const c = candles[i];
    const pc = candles[i - 1].close;
    if (![c.high, c.low, pc].every(Number.isFinite)) continue;
    tr.push(Math.max(c.high, pc) - Math.min(c.low, pc));
  }
  if (tr.length < minBars) return null;
  tr.sort((a, b) => a - b);
  const m = tr.length >> 1;
  return tr.length % 2 ? tr[m] : (tr[m - 1] + tr[m]) / 2;
}

/**
 * The NQ-point tolerance pref as the same multiple of another market's median
 * true range (`nqPts / NQ_REF_MEDIAN_TRUE_RANGE_PTS` bars), rounded to the
 * market's tick, never below one tick. With too few bars, the market's price
 * times NQ's true range / price stands in. null when there is nothing to
 * scale by (no candles).
 */
export function scaledConfluenceTolerance(nqPts: number, candles: readonly Hlc[], tick: number): number | null {
  if (!(nqPts > 0) || !(tick > 0)) return null;
  let atr = medianTrueRange(candles);
  if (atr === null || !(atr > 0)) {
    const last = candles.length > 0 ? candles[candles.length - 1].close : NaN;
    if (!(last > 0)) return null;
    atr = (last * NQ_REF_MEDIAN_TRUE_RANGE_PTS) / NQ_REF_PRICE;
  }
  return Math.max(tick, roundToTick((nqPts * atr) / NQ_REF_MEDIAN_TRUE_RANGE_PTS, tick));
}

/**
 * The stack tolerance a chart uses. Non-Vest symbols: the pref itself (NQ
 * points), unchanged. Vest feeds: `scaledConfluenceTolerance` on the chart's
 * raw closed base bars, 0 (no zones) if it cannot be scaled yet.
 */
export function confluenceToleranceFor(symbol: string | null | undefined, nqPts: number, candles: readonly Hlc[], tick: number | null): number {
  if (!isVestFeedSymbol(symbol)) return nqPts;
  return scaledConfluenceTolerance(nqPts, candles, tick ?? 0.01) ?? 0;
}

/** The zone label for a chart. Vest feeds never claim absorption odds: the calibration is NQ's. */
export function confluenceLabelFor(symbol: string | null | undefined): string {
  return isVestFeedSymbol(symbol) ? 'EXTREME · stacked levels' : confluenceLabel();
}

/** Context a Vest zone tooltip needs: the market's tick and the tolerance it was grouped with. */
export interface VestConfluenceContext {
  tick: number;
  tolerance: number;
  nqTolerancePts: number;
}

/**
 * Tooltip lines for a chart. Non-Vest symbols: `confluenceTooltip` exactly.
 * Vest feeds: the stack in the market's own decimals, how the tolerance was
 * derived, and that no stack history exists for this market (no NQ figures).
 */
export function confluenceTooltipFor(symbol: string | null | undefined, zone: ConfluenceZone, vest?: VestConfluenceContext): string[] {
  if (!isVestFeedSymbol(symbol)) return confluenceTooltip(zone);
  const d = vest ? tickDecimals(vest.tick) : 2;
  const members = zone.members
    .slice()
    .sort((a, b) => b.price - a.price)
    .map((m) => `${m.label} ${m.price.toFixed(d)}`)
    .join(' · ');
  const lines = [`${zone.members.length} levels within ${(zone.high - zone.low).toFixed(d)} pts: ${members}`];
  if (vest) {
    lines.push(
      `Tolerance ${vest.tolerance.toFixed(d)} pts: the ${vest.nqTolerancePts} NQ pt setting scaled by this market's median 2m true range.`,
    );
  }
  lines.push('Uncalibrated: no stack hold history was measured for this market, so there is no measured edge either way.');
  return lines;
}
